import { createTheme } from '@mui/material/styles';

type ThemeSize = 'small' | 'medium' | 'large' | 'xlarge';

/** Large is the old Default and X-Large the old Large, value for value. */
const FONT_SIZE_MAP: Record<ThemeSize, number> = {
  small: 12,
  medium: 13,
  large: 13,
  xlarge: 15,
};

/** The touch query from responsive.css, which gives every button a 44px
 *  minimum there. The control-height minimums below are emotion styles and
 *  beat that stylesheet rule, so they restate the 44px for themselves. */
export const TOUCH_MEDIA = '@media (pointer: coarse), (max-width: 1024px)';

export function buildMuiTheme(
  darkMode: boolean,
  themeSize: ThemeSize = 'medium',
  ui2026 = false,
) {
  const fontSize = FONT_SIZE_MAP[themeSize];

  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      // The --primary-blue / --on-primary pair from cssVariables.css (MUI needs
      // literals to derive its hover and focus shades). Light: white on #2563eb,
      // 5.17:1 (was #3b82f6, 3.68:1). Dark: a light blue that reads as text on
      // the dark surfaces, so a filled button carries dark text (7.02:1).
      primary: darkMode
        ? { main: '#60a5fa', contrastText: '#0f172a' }
        : { main: '#2563eb', contrastText: '#ffffff' },
      background: {
        default: darkMode ? '#121212' : '#f8f9fa',
        paper: darkMode ? '#1f1f1f' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#e4e4e4' : '#333333',
        secondary: darkMode ? '#a0a0a0' : '#666666',
      },
    },
    typography: {
      // Same token as the stylesheets. Hardcoding the stack here meant MUI
      // components kept the old face when the theme changed it everywhere else.
      fontFamily: 'var(--font-sans)',
      fontSize,
      // MUI derives every variant from a coefficient of `fontSize / 14`. At
      // fontSize 13 that is 0.9286, which turns MUI's 16 / 18 / 20px defaults
      // into 14.857 / 16.714 / 18.571px - three of the nine sizes measured on
      // a single page, none of them chosen by anyone.
      //
      // Pinning the variants to the CSS scale bypasses the coefficient
      // entirely, so MUI and the stylesheets agree on what "small" means.
      ...(ui2026
        ? {
            body1: { fontSize: 'var(--font-size-base)' },
            body2: { fontSize: 'var(--font-size-small)' },
            button: { fontSize: 'var(--font-size-small)' },
            subtitle1: { fontSize: 'var(--font-size-base)' },
            subtitle2: { fontSize: 'var(--font-size-small)' },
            caption: { fontSize: 'var(--text-12)' },
            h6: { fontSize: 'var(--font-size-large)' },
          }
        : {}),
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            // Buttons measured at 27, 30, 31, 32 and 34px on one form row,
            // because nothing consumed --button-height; a dialog's footer
            // buttons still came out at 38px (15px text x MUI's 1.75 line
            // height + 6px padding). The standard control height, with the
            // padding moved to the sides, lands every size and variant on the
            // same line as the fields beside it (cssVariables.css).
            ...(ui2026
              ? {
                  minHeight: 'var(--control-height)',
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 'var(--control-padding-x)',
                  paddingRight: 'var(--control-padding-x)',
                  lineHeight: 1.25,
                  // The Basic editor's Today button measured 32px on a phone.
                  [TOUCH_MEDIA]: { minHeight: 44 },
                }
              : {}),
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },
      // The field edge: MUI's rgba(0,0,0,0.23) / rgba(255,255,255,0.23) and
      // the --border-color hairline that globalStyles.css forced over it were
      // 1.3:1 at best, under the 3:1 WCAG 1.4.11 asks of a control boundary.
      // Tokens from cssVariables.css, so the four palettes and high contrast
      // each set their own; globalStyles.css applies the same ones.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--control-border-hover)',
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--control-border-disabled)',
            },
          },
          notchedOutline: {
            borderColor: 'var(--control-border)',
          },
        },
      },
      // Icon buttons sized themselves from their content, so a row of them in
      // the toolbar came out at 27 / 30 / 31 / 32px depending on what was
      // inside - a bare icon, an icon with a badge, an icon with text. Square
      // and pinned to the same height as everything else on a control row.
      MuiIconButton: {
        styleOverrides: {
          root: {
            ...(ui2026
              ? {
                  width: 'var(--control-height)',
                  height: 'var(--control-height)',
                }
              : {}),
          },
        },
      },
      // Toggle buttons (segmented groups) at the control height, with the
      // buttons' text size. Exactly the fields' height (minHeight let a group
      // sit taller than the fields beside it when its text wrapped), and the
      // buttons' side padding plus 4px so a two-word label is not boxed in.
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            ...(ui2026
              ? {
                  minHeight: 'var(--control-height)',
                  height: 'var(--control-height)',
                  paddingTop: 0,
                  paddingBottom: 0,
                  lineHeight: 1.25,
                  fontSize: 'var(--control-font-size)',
                  paddingLeft: 'calc(var(--control-padding-x) + 4px)',
                  paddingRight: 'calc(var(--control-padding-x) + 4px)',
                  whiteSpace: 'nowrap',
                  [TOUCH_MEDIA]: { minHeight: 44, height: 'auto' },
                  color: 'var(--text-secondary)',
                  borderColor: 'var(--border-color)',
                  // Selected: the app's blue tint (the toolbar toggles'
                  // look), not MUI's grey, which read as a pressed box.
                  '&.Mui-selected, &.Mui-selected:hover': {
                    // --primary-blue-hover on the tint: #1d4ed8 6.0:1 (light); #93c5fd 7.8:1 on the dark paper.
                    color: 'var(--primary-blue-hover)',
                    backgroundColor: 'var(--primary-blue-tint)',
                  },
                }
              : {}),
          },
        },
      },
      // Dialog insets: one padding on every edge, title to footer, in the 2026
      // and the classic UI alike (globalStyles.css sets the variables on the
      // dialog paper; docs there). The fallbacks are MUI's own defaults, so
      // outside a dialog nothing changes, and a dialog's own sx padding still
      // wins.
      MuiDialogTitle: {
        styleOverrides: {
          root: { padding: 'var(--dialog-title-padding, 16px 24px)' },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ ownerState }) =>
            ownerState.dividers
              ? { padding: 'var(--dialog-content-padding-dividers, 16px 24px)' }
              : {
                  padding: 'var(--dialog-content-padding, 20px 24px)',
                  '.MuiDialogTitle-root + &': { paddingTop: 'var(--dialog-content-padding-top-after-title, 0px)' },
                },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: 'var(--dialog-actions-padding, 8px)' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      // Tooltips default to 11px in MUI, which is uncomfortably
      // small against our base 14-15px text. Bump to the same
      // `--font-size-small` token the rest of the secondary
      // copy uses so tooltip text is readable without leaning
      // in. Padding nudged up proportionally.
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 'var(--font-size-small, 13px)',
            lineHeight: 1.4,
            padding: '6px 10px',
            maxWidth: 360,
          },
          arrow: {
            // Keep arrow proportional to the larger bubble.
            fontSize: 14,
          },
        },
      },
    },
  });
}
